import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { GeographicLevel } from 'app/shared/model/enumerations/geographic-level.model';
import { createEntity, getEntity, reset, updateEntity } from './tenure.reducer';

export const TenureUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const tenureEntity = useAppSelector(state => state.tenure.entity);
  const loading = useAppSelector(state => state.tenure.loading);
  const updating = useAppSelector(state => state.tenure.updating);
  const updateSuccess = useAppSelector(state => state.tenure.updateSuccess);
  const geographicLevelValues = Object.keys(GeographicLevel);

  const handleClose = () => {
    navigate(`/tenure${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.totalAllHouseholds !== undefined && typeof values.totalAllHouseholds !== 'number') {
      values.totalAllHouseholds = Number(values.totalAllHouseholds);
    }
    if (values.ownedOutright !== undefined && typeof values.ownedOutright !== 'number') {
      values.ownedOutright = Number(values.ownedOutright);
    }
    if (values.ownedMortgageLoan !== undefined && typeof values.ownedMortgageLoan !== 'number') {
      values.ownedMortgageLoan = Number(values.ownedMortgageLoan);
    }
    if (values.sharedOwnership !== undefined && typeof values.sharedOwnership !== 'number') {
      values.sharedOwnership = Number(values.sharedOwnership);
    }
    if (values.socialRentedCouncil !== undefined && typeof values.socialRentedCouncil !== 'number') {
      values.socialRentedCouncil = Number(values.socialRentedCouncil);
    }
    if (values.socialRentedOther !== undefined && typeof values.socialRentedOther !== 'number') {
      values.socialRentedOther = Number(values.socialRentedOther);
    }
    if (values.privateRented !== undefined && typeof values.privateRented !== 'number') {
      values.privateRented = Number(values.privateRented);
    }
    if (values.livesRentFree !== undefined && typeof values.livesRentFree !== 'number') {
      values.livesRentFree = Number(values.livesRentFree);
    }

    const entity = {
      ...tenureEntity,
      ...values,
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          geographicLevel: 'EXTRA_WARD',
          ...tenureEntity,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="apiLocationApp.tenure.home.createOrEditLabel" data-cy="TenureCreateUpdateHeading">
            <Translate contentKey="apiLocationApp.tenure.home.createOrEditLabel">Create or edit a Tenure</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="tenure-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('apiLocationApp.tenure.geographicLevel')}
                id="tenure-geographicLevel"
                name="geographicLevel"
                data-cy="geographicLevel"
                type="select"
              >
                {geographicLevelValues.map(geographicLevel => (
                  <option value={geographicLevel} key={geographicLevel}>
                    {translate(`apiLocationApp.GeographicLevel.${geographicLevel}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('apiLocationApp.tenure.date')}
                id="tenure-date"
                name="date"
                data-cy="date"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.geography')}
                id="tenure-geography"
                name="geography"
                data-cy="geography"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.geographyCode')}
                id="tenure-geographyCode"
                name="geographyCode"
                data-cy="geographyCode"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.totalAllHouseholds')}
                id="tenure-totalAllHouseholds"
                name="totalAllHouseholds"
                data-cy="totalAllHouseholds"
                type="text"
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.ownedOutright')}
                id="tenure-ownedOutright"
                name="ownedOutright"
                data-cy="ownedOutright"
                type="text"
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.ownedMortgageLoan')}
                id="tenure-ownedMortgageLoan"
                name="ownedMortgageLoan"
                data-cy="ownedMortgageLoan"
                type="text"
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.sharedOwnership')}
                id="tenure-sharedOwnership"
                name="sharedOwnership"
                data-cy="sharedOwnership"
                type="text"
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.socialRentedCouncil')}
                id="tenure-socialRentedCouncil"
                name="socialRentedCouncil"
                data-cy="socialRentedCouncil"
                type="text"
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.socialRentedOther')}
                id="tenure-socialRentedOther"
                name="socialRentedOther"
                data-cy="socialRentedOther"
                type="text"
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.privateRented')}
                id="tenure-privateRented"
                name="privateRented"
                data-cy="privateRented"
                type="text"
              />
              <ValidatedField
                label={translate('apiLocationApp.tenure.livesRentFree')}
                id="tenure-livesRentFree"
                name="livesRentFree"
                data-cy="livesRentFree"
                type="text"
              />
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/tenure" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TenureUpdate;
